/* eslint-disable no-param-reassign */
import axios from 'src/utils/axios';
import { encrypt } from 'src/utils/encryption';
import { appConfig } from 'src/config';
import { createSlice } from '@reduxjs/toolkit';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  isAuthenticated: false,
  user: {},
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // INITIALISE
    getInitialize(state, action) {
      state.isLoading = false;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.user = action.payload.user;
    },

    // LOGIN
    loginSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
    },

    // REGISTER
    registerSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
    },

    // LOGOUT
    logoutSuccess(state) {
      state.isAuthenticated = false;
      state.user = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Export action for cross-tab logout listener
export const { logoutSuccess } = slice.actions;

// ----------------------------------------------------------------------

export function login({ email, password, rememberMe }) {
  return async (dispatch) => {
    const ep = encrypt(appConfig.appKey, password); // encrypted password

    const response = await axios.post('/v2/auth/login', {
      email,
      ep,
      rememberMe: rememberMe || false,
      clientType: 'admin',
    });

    const { accessToken, ...user } = response.data.result;

    // Defense-in-depth: verify admin access client-side
    if (!user.isAdmin) {
      // eslint-disable-next-line no-throw-literal
      throw { code: 'FORBIDDEN', message: 'You do not have admin access.' };
    }

    // Store access token in sessionStorage
    sessionStorage.setItem('accessToken', accessToken);

    dispatch(
      slice.actions.loginSuccess({
        user: {
          ...user,
          displayName: `${user?.firstName} ${user?.lastName}`,
        },
      }),
    );
  };
}

// ----------------------------------------------------------------------

export function register({ email, password, firstName, lastName }) {
  return async (dispatch) => {
    // Not implemented
    const response = await axios.post('/auth/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    sessionStorage.setItem('accessToken', accessToken);
    dispatch(slice.actions.registerSuccess({ user }));
  };
}

// ----------------------------------------------------------------------

export function logout() {
  return async (dispatch) => {
    try {
      await axios.post('/v2/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    // Always clear auth state regardless of API response
    sessionStorage.removeItem('accessToken');
    dispatch(slice.actions.logoutSuccess());

    // Signal other tabs to logout via localStorage event
    localStorage.setItem('logout_event', Date.now().toString());
  };
}

// ----------------------------------------------------------------------

export function getInitialize() {
  return async (dispatch, getState) => {
    dispatch(slice.actions.startLoading());

    try {
      const accessToken = sessionStorage.getItem('accessToken');
      const { isAuthenticated } = getState().auth;

      // If no access token AND not previously authenticated (via redux-persist),
      // the user is genuinely not logged in — skip the API call to avoid
      // an infinite redirect loop (401 → refresh fail → redirect → remount)
      if (!accessToken && !isAuthenticated) {
        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: false,
            user: null,
          }),
        );
        return;
      }

      // Call /v2/auth/me to get user data
      // Even if sessionStorage has no token (e.g. new tab), the Axios
      // interceptor will attempt /v2/auth/refresh-token using the
      // shared HttpOnly cookie, then retry this request
      const response = await axios.get('/v2/auth/me');
      const user = response.data.result;

      dispatch(
        slice.actions.getInitialize({
          isAuthenticated: true,
          user: {
            ...user,
            displayName: `${user?.firstName} ${user?.lastName}`,
          },
        }),
      );
    } catch (error) {
      console.error(error);
      dispatch(
        slice.actions.getInitialize({
          isAuthenticated: false,
          user: null,
        }),
      );
    }
  };
}
